import React, { useState } from "react";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  Chip,
  Stack,
  Grid,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { CustomButton } from "@ippon/shared-components";
import { editIcon, documentIcon } from "@ippon/shared-assets";
import LotBoundaryModal from "./LotBoundaryModal";

interface Boundary {
  adjacentLand: string;
  confirmation: any[];
  encroachment: string;
  encroachmentMemo: any[];
}

interface Lot {
  lotNumber: string;
  boundaries: Boundary[];
}

interface LotBoundaryManagerProps {
  lots: Lot[];
  onLotsChange: (lots: Lot[]) => void;
}

const LotBoundaryManager: React.FC<LotBoundaryManagerProps> = ({
  lots,
  onLotsChange,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [modalLot, setModalLot] = useState<Lot>({
    lotNumber: "",
    boundaries: [
      {
        adjacentLand: "",
        confirmation: [],
        encroachment: "",
        encroachmentMemo: [],
      },
    ],
  });

  // URLまたはFileオブジェクトからファイル名を抽出（完全版）
  const getFullFileNameFromDoc = (doc: any): string => {
    let fileName = "";

    // CustomFileUploaderから返されるデータ構造の場合
    if (doc.originalFileName && typeof doc.originalFileName === "string") {
      fileName = doc.originalFileName;
    }
    // Fileオブジェクトの場合
    else if (doc instanceof File) {
      fileName = doc.name;
    }
    // nameプロパティがある場合
    else if (doc.name && typeof doc.name === "string") {
      fileName = doc.name;
    }
    // CustomFileUploaderが返す既存URL形式: {id: "...", data: "url", isUrl: true}
    else if (doc && doc.isUrl && doc.data && typeof doc.data === "string") {
      try {
        const urlObj = new URL(doc.data);
        const pathname = urlObj.pathname;
        const parts = pathname.split("/");
        const lastPart = parts[parts.length - 1];
        fileName = decodeURIComponent(lastPart);
      } catch {
        const pathWithoutQuery = doc.data.split("?")[0];
        const extractedName =
          pathWithoutQuery.split("/").pop() || "document.pdf";
        try {
          fileName = decodeURIComponent(extractedName);
        } catch {
          fileName = extractedName;
        }
      }
    }
    // URL文字列の場合
    else if (typeof doc === "string") {
      try {
        const urlObj = new URL(doc);
        const pathname = urlObj.pathname;
        const parts = pathname.split("/");
        const lastPart = parts[parts.length - 1];
        fileName = decodeURIComponent(lastPart);
      } catch {
        const pathWithoutQuery = doc.split("?")[0];
        const extractedName =
          pathWithoutQuery.split("/").pop() || "document.pdf";
        try {
          fileName = decodeURIComponent(extractedName);
        } catch {
          fileName = extractedName;
        }
      }
    }
    // doc.urlプロパティがある場合
    else if (doc.url) {
      try {
        const urlObj = new URL(doc.url);
        const pathname = urlObj.pathname;
        const parts = pathname.split("/");
        const lastPart = parts[parts.length - 1];
        fileName = decodeURIComponent(lastPart);
      } catch {
        const pathWithoutQuery = doc.url.split("?")[0];
        const extractedName =
          pathWithoutQuery.split("/").pop() || "document.pdf";
        try {
          fileName = decodeURIComponent(extractedName);
        } catch {
          fileName = extractedName;
        }
      }
    }

    return fileName;
  };

  // URLまたはFileオブジェクトからファイル名を抽出（短縮版）
  const getFileNameFromDoc = (doc: any): string => {
    const fileName = getFullFileNameFromDoc(doc);

    // 15文字で切り詰める
    if (fileName.length > 15) {
      return fileName.slice(0, 15) + "...";
    }

    return fileName;
  };

  // モーダルを開く（追加）
  const handleOpenAddModal = () => {
    setEditingIndex(null);
    setModalLot({
      lotNumber: "",
      boundaries: [
        {
          adjacentLand: "",
          confirmation: [],
          encroachment: "",
          encroachmentMemo: [],
        },
      ],
    });
    setIsModalOpen(true);
  };

  // モーダルを開く（編集）
  const handleOpenEditModal = (index: number) => {
    setEditingIndex(index);
    const copiedLot = JSON.parse(JSON.stringify(lots[index])); // ディープコピー
    setModalLot(copiedLot);
    setIsModalOpen(true);
  };

  // モーダルを閉じる
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingIndex(null);
  };

  // 保存
  const handleSave = (lot: Lot) => {
    const newLots = [...lots];
    if (editingIndex !== null) {
      // 編集
      newLots[editingIndex] = lot;
    } else {
      // 追加
      newLots.push(lot);
    }
    onLotsChange(newLots);
    handleCloseModal();
  };

  // 削除
  const handleDelete = (index: number) => {
    const newLots = lots.filter((_, i) => i !== index);
    onLotsChange(newLots);
  };

  // モーダル内の境界追加
  const addBoundaryInModal = () => {
    setModalLot((prev) => ({
      ...prev,
      boundaries: [
        ...prev.boundaries,
        {
          adjacentLand: "",
          confirmation: [],
          encroachment: "",
          encroachmentMemo: [],
        },
      ],
    }));
  };

  // モーダル内の境界削除
  const removeBoundaryInModal = (boundaryIndex: number) => {
    setModalLot((prev) => ({
      ...prev,
      boundaries: prev.boundaries.filter((_, i) => i !== boundaryIndex),
    }));
  };

  return (
    <Box>
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 700,
          mb: 2,
        }}
      >
        地番・境界
      </Typography>

      <Box sx={{ mb: 2 }}>
        <CustomButton
          label="＋地番を追加"
          buttonCategory="clearPrimary"
          onClick={handleOpenAddModal}
          sx={{ width: { xs: "100%", sm: "600px" } }}
        />
      </Box>

      {/* アコーディオン表示（既存データ） */}
      {lots.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {lots.map((lot, lotIndex) => {
              return (
                <Box
                  key={`lot-wrapper-${lotIndex}`}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    mb: 1.5,
                    width: { xs: "100%", sm: "100%", md: "auto" },
                  }}
                >
                  <Accordion
                    className="lot-boundary-accordion"
                    expanded={expandedIds.has(lotIndex)}
                    onChange={(_e, isExpanded) => {
                      setExpandedIds((prev) => {
                        const next = new Set(prev);
                        if (isExpanded) {
                          next.add(lotIndex);
                        } else {
                          next.delete(lotIndex);
                        }
                        return next;
                      });
                    }}
                    sx={{
                      borderRadius: "10px !important",
                      "&:before": { display: "none" },
                      border: "1px solid #D9D9D9",
                      boxShadow: "none",
                      width: { xs: "100%", sm: "600px" },
                      position: "relative",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={null}
                      sx={{
                        flexDirection: "row",
                        position: "relative",
                        paddingLeft: 0.7,
                        "& .MuiAccordionSummary-content": {
                          margin: "1px 0",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          overflow: "hidden",
                        },
                      }}
                    >
                      {/* 削除ボタン（右端上） */}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(lotIndex);
                        }}
                        sx={{
                          position: "absolute",
                          top: "50%",
                          transform: "translateY(-50%)",
                          right: 10,
                          color: "#666",
                          zIndex: 1,
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: "16px" }} />
                      </IconButton>
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedIds((prev) => {
                            const next = new Set(prev);
                            if (expandedIds.has(lotIndex)) {
                              next.delete(lotIndex);
                            } else {
                              next.add(lotIndex);
                            }
                            return next;
                          });
                        }}
                        sx={{
                          width: 26,
                          height: 26,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "8px",
                          cursor: "pointer",
                          backgroundColor: expandedIds.has(lotIndex)
                            ? "#DBF8FF"
                            : "transparent",
                          color: expandedIds.has(lotIndex)
                            ? "#0E9DBC"
                            : "#3e3e3e",
                          transition: "all 0.2s",
                          flexShrink: 0,
                          "&:hover": {
                            backgroundColor: "#DBF8FF",
                            "& svg": {
                              color: "#0E9DBC",
                            },
                          },
                        }}
                      >
                        <ExpandMoreIcon
                          sx={{
                            transform: expandedIds.has(lotIndex)
                              ? "rotate(0deg)"
                              : "rotate(-90deg)",
                            fontSize: 20,
                            color: "inherit",
                            transition: "transform 0.2s ease",
                          }}
                        />
                      </Box>
                      {/* 左側グループ：地番+編集ボタン（縮まない） */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          ml: -0.6,
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                          }}
                        >
                          地番：{lot.lotNumber || "未入力"}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEditModal(lotIndex);
                          }}
                          sx={{
                            color: "#666",
                            flexShrink: 0,
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          <img
                            src={editIcon}
                            alt="edit"
                            width={18}
                            height={18}
                            style={{ opacity: 0.8, filter: "grayscale(100%)" }}
                          />
                        </IconButton>
                      </Box>
                      {/* 右側グループ：チップ群（狭い画面では右から隠れる） */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexShrink: 1,
                          minWidth: 0,
                          overflow: "hidden",
                        }}
                      >
                        {lot.boundaries.length > 0 && (
                          <Chip
                            label={`境界${lot.boundaries.length}件`}
                            size="small"
                            sx={{
                              height: "20px",
                              fontSize: "11px",
                              fontWeight: 700,
                              backgroundColor: "#E3F2FD",
                              color: "#0B9DBD",
                              flexShrink: 0,
                              "&:hover": {
                                backgroundColor: "#E3F2FD",
                              },
                            }}
                          />
                        )}
                        {(() => {
                          const confirmationCount = lot.boundaries.reduce(
                            (sum, b) => sum + (b.confirmation?.length || 0),
                            0
                          );
                          return (
                            confirmationCount > 0 && (
                              <Chip
                                label={`確定書類${confirmationCount}件`}
                                size="small"
                                sx={{
                                  height: "20px",
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  backgroundColor: "#F5F5F5",
                                  color: "#6B7280",
                                  flexShrink: 0,
                                  "&:hover": {
                                    backgroundColor: "#F5F5F5",
                                  },
                                }}
                              />
                            )
                          );
                        })()}
                        {(() => {
                          const encroachmentMemoCount = lot.boundaries.reduce(
                            (sum, b) => sum + (b.encroachmentMemo?.length || 0),
                            0
                          );
                          return (
                            encroachmentMemoCount > 0 && (
                              <Chip
                                label={`越境覚書${encroachmentMemoCount}件`}
                                size="small"
                                sx={{
                                  height: "20px",
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  backgroundColor: "rgba(230, 149, 0, 0.1)",
                                  color: "#E69500",
                                  flexShrink: 0,
                                  "&:hover": {
                                    backgroundColor: "rgba(230, 149, 0, 0.1)",
                                  },
                                }}
                              />
                            )
                          );
                        })()}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        pt: 0,
                        pb: 1.5,
                      }}
                    >
                      <Box sx={{ px: 0 }}>
                        {lot.boundaries.map((boundary, boundaryIndex) => (
                          <Box
                            key={`boundary-display-${boundaryIndex}`}
                            sx={{
                              px: 1.5,
                              py: 1.5,
                              backgroundColor: "#f9fafb",
                              borderRadius: "8px",
                              mb:
                                boundaryIndex < lot.boundaries.length - 1
                                  ? 1
                                  : 0,
                            }}
                          >
                            <Grid container spacing={0}>
                              {/* 隣地 */}
                              <Grid item xs={12} sm={6} md={2}>
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 700,
                                    mb: 0.5,
                                    color: "#3e3e3e",
                                  }}
                                >
                                  隣地
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    color: "#3e3e3e",
                                    fontWeight: 700,
                                  }}
                                >
                                  {boundary.adjacentLand || "-"}
                                </Typography>
                              </Grid>

                              {/* 確定の書類 */}
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 700,
                                    mb: 0.5,
                                    color: "#3e3e3e",
                                  }}
                                >
                                  確定書類
                                </Typography>
                                {boundary.confirmation &&
                                boundary.confirmation.length > 0 ? (
                                  <Stack spacing={0.5}>
                                    {boundary.confirmation.map(
                                      (doc: any, docIndex: number) => (
                                        <Tooltip
                                          key={`conf-doc-${docIndex}`}
                                          title={getFullFileNameFromDoc(doc)}
                                          placement="top"
                                          arrow
                                        >
                                          <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                          >
                                            <Box
                                              component="img"
                                              src={documentIcon}
                                              width={16}
                                              height={16}
                                              alt="doc"
                                            />
                                            <Typography
                                              fontSize={12}
                                              fontWeight={700}
                                              color="#3e3e3e"
                                            >
                                              {getFileNameFromDoc(doc)}
                                            </Typography>
                                          </Stack>
                                        </Tooltip>
                                      )
                                    )}
                                  </Stack>
                                ) : (
                                  <Typography
                                    sx={{ fontSize: "13px", color: "#999" }}
                                  >
                                    -
                                  </Typography>
                                )}
                              </Grid>

                              {/* 越境 */}
                              <Grid item xs={12} sm={6} md={2}>
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 700,
                                    mb: 0.5,
                                    color: "#3e3e3e",
                                  }}
                                >
                                  越境
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    color: "#3e3e3e",
                                    fontWeight: 700,
                                  }}
                                >
                                  {boundary.encroachment || "-"}
                                </Typography>
                              </Grid>

                              {/* 越境覚書 */}
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 700,
                                    mb: 0.5,
                                    color: "#3e3e3e",
                                  }}
                                >
                                  越境覚書
                                </Typography>
                                {boundary.encroachmentMemo &&
                                boundary.encroachmentMemo.length > 0 ? (
                                  <Stack spacing={0.5}>
                                    {boundary.encroachmentMemo.map(
                                      (doc: any, docIndex: number) => (
                                        <Tooltip
                                          key={`memo-doc-${docIndex}`}
                                          title={getFullFileNameFromDoc(doc)}
                                          placement="top"
                                          arrow
                                        >
                                          <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                          >
                                            <Box
                                              component="img"
                                              src={documentIcon}
                                              width={16}
                                              height={16}
                                              alt="doc"
                                            />
                                            <Typography
                                              fontSize={12}
                                              fontWeight={700}
                                              color="#3e3e3e"
                                            >
                                              {getFileNameFromDoc(doc)}
                                            </Typography>
                                          </Stack>
                                        </Tooltip>
                                      )
                                    )}
                                  </Stack>
                                ) : (
                                  <Typography
                                    sx={{ fontSize: "13px", color: "#999" }}
                                  >
                                    -
                                  </Typography>
                                )}
                              </Grid>
                            </Grid>
                          </Box>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              );
            })}
          </Box>
        )}

      {/* モーダル（追加・編集） */}
      <LotBoundaryModal
        open={isModalOpen}
        onClose={handleCloseModal}
        editingIndex={editingIndex}
        modalLot={modalLot}
        onSave={handleSave}
        addBoundaryInModal={addBoundaryInModal}
        removeBoundaryInModal={removeBoundaryInModal}
      />
    </Box>
  );
};

export default LotBoundaryManager;
